import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TaskLibraryMigrateDialogComponent } from 'app/dialogs/task-library-migrate-dialog/task-library-migrate-dialog.component';

import * as models from 'app/models';
import * as services from 'app/services';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-task-library-list',
    templateUrl: './task-library-list.component.html',
    styleUrls: ['./task-library-list.component.scss'],
    standalone: false
})
export class TaskLibraryListComponent implements OnInit, OnDestroy {

  @Input() selectionLocked: boolean = false;
  @Input() selectedId: string;
  @Output() loadedList: EventEmitter<models.TemplatedTask[]> = new EventEmitter<models.TemplatedTask[]>();
  companySub: Subscription;

  templatedTasks: models.TemplatedTask[];
  userRole: models.Role;

  /** Flat rows for the p-table, rebuilt whenever templatedTasks is (re)assigned. */
  tableRows: Array<any> = [];
  tableLoading: boolean = true;

  get canAdd() : boolean {
    return this.userRole?.permissions?.includes(models.Permissions.addTemplate);
  }

  get canDelete(): boolean {
    return this.userRole?.permissions?.includes(models.Permissions.deleteTemplate);
  }

  constructor(
    private helperService: services.HelperService
    , private templatedTaskService: services.TemplatedTasksService
    , private claimsService: services.ClaimsService
    , private dialog: MatDialog
  ) { }

  async ngOnInit(): Promise<void> {
    this.load();
    this.userRole = await this.claimsService.getRoleAwait();
  }

  ngOnDestroy() {
    if(this.companySub != null){
      this.companySub.unsubscribe();
    }
  }

  load() {
    //get companyId
    let companyId = this.helperService.currentCompanyId;
    //if we have it then load
    if(companyId != null){
      this.getTaskLibrary();
    }

    //set up subscription to listen to company id changes
    this.companySub = this.helperService.getCurrentCompanyId().subscribe(data => {
      this.getTaskLibrary();
    })
  }

  buildTableRows() {
    this.tableRows = (this.templatedTasks || []).map(t => ({ ...t }));
  }

  selectTask(row: any) {
    if (this.selectionLocked) { return; }        // editor dirty → selection frozen
    if (this.selectedId == 'add') { return; }    // adding a new task → selection frozen
    if (row?.id == null || row.id == this.selectedId) { return; }

    this.selectedId = row.id;
    this.helperService.addQueryStringParam(models.queryStringKeys.Templatedtask, this.selectedId);
  }

  getTaskLibrary() {
    this.tableLoading = true;
    this.templatedTaskService.getAll().then(data => {
      this.templatedTasks = this.helperService.sortArrayByStringField(data, 'name');
      this.buildTableRows();
      this.tableLoading = false;
      this.loadedList.emit(this.templatedTasks);
    });
  }

  refresh(templatedTask: models.TemplatedTask | null) {
    let list = this.helperService.deepCopy(this.templatedTasks);

    if(templatedTask == null){ //this means it was a delete
      list = list.filter(i => i.id != this.selectedId);
    } else {
      let index = this.templatedTasks.findIndex(i => i.id == templatedTask.id);
      if(index > -1){
        list[index] = templatedTask;
      } else {
        list.push(templatedTask);
      }
    }
    this.templatedTasks = this.helperService.sortArrayByStringField(list, 'name');
    this.buildTableRows();
    this.loadedList.emit(this.templatedTasks);
  }

  add() {
    this.helperService.addQueryStringParam(models.queryStringKeys.Templatedtask, 'add');
  }

  migrate() {
    this.dialog.open(TaskLibraryMigrateDialogComponent, {
      data: this.templatedTasks
    });
  }

  exportAsCSV() {
    // Build the CSV from the visible p-table columns.
    const cols: Array<{ header: string, value: (r: any) => string }> = [
      { header: 'Name', value: r => r.name ?? '' },
    ];

    const esc = (s: any) => '"' + String(s ?? '').replace(/"/g, '""') + '"';
    const lines = [cols.map(c => esc(c.header)).join(',')];
    this.tableRows.forEach(r => lines.push(cols.map(c => esc(c.value(r))).join(',')));

    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'task-library.csv';
    a.click();
    URL.revokeObjectURL(url);
  }
}
